import React, { useEffect, useMemo, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Button from 'react-bootstrap/Button'
import FuncFetch from '../../Utilities/funcFetch'
import useFetch from '../../Utilities/useFetch'
import { useMsal } from '@azure/msal-react'
import { MdEmail } from 'react-icons/md'
import { Alert, ButtonGroup, Spinner } from 'react-bootstrap'
import Modal from '../../Utilities/DraggableModal'
import Select from 'react-select'
import { PiPencil } from 'react-icons/pi'
import { BiTrash } from 'react-icons/bi'
import { FaListUl } from 'react-icons/fa'
import { FaRobot } from 'react-icons/fa'
import EditNoteModal from './EditNoteModal'
import DeleteNoteConfirmationModal from './DeleteNoteConfirmationModal'

function Comments({ 
    activities,
    setActivities, 
    debtorID,
    account,
    aiSummary,
    onOpenTranscripts,
    hasTranscripts,
    updateAccountFieldsAndActivities,
    }) {
    const [loader, setLoader] = useState(false);
    const [activityList, setActivityList] = useState();
    const [searchString, setSearchString] = useState();
    const [error, setError] = useState();
    const [notes, setNotes] = useState('');
    const [selectedNote, setSelectedNote] = useState();
    const [editNotePopupOpen,setEditNotePopupOpen] = useState(false);
    const [deleteNoteConfirmationPopupOpen, setDeleteNoteConfirmationPopupOpen] = useState(false);
    const [isPublic, setIsPublic] = useState(true);
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    const [selectedAIPrompt, setSelectedAIPrompt] = useState(null);
    const [runningAIPrompt, setRunningAIPrompt] = useState(false);
    const [aiPromptError, setAIPromptError] = useState();
    const [generatedAISummary, setGeneratedAISummary] = useState();
    const [aiSummaryHistoryPopupOpen, setAISummaryHistoryPopupOpen] = useState(false);
    const [aiSummaryHistory, setAISummaryHistory] = useState([]);
    const [loadingAISummaryHistory, setLoadingAISummaryHistory] = useState(false);
    const [aiSummaryHistoryError, setAISummaryHistoryError] = useState();
    const { REACT_APP_API_URL } = process.env;
    const { instance, accounts } = useMsal();
    const initialRecoveryPercent = account?.recoveryPercent ?? account?.recoverypercent ?? 0;
    const [recoveryPercent, setRecoveryPercent] = useState(initialRecoveryPercent ?? 0);
    const projectIdValue = account?.projectID ?? account?.projectId;
    const { data: aiPrompts, loading: loadingAIPrompts } = useFetch(
        true,
        `${REACT_APP_API_URL}/Claims/GetAIPrompts`,
        'GET',
        null,
        projectIdValue ? { projectID: projectIdValue } : null
    );

    const aiPromptOptions = useMemo(() => {
        if (!Array.isArray(aiPrompts)) {
            return [];
        }

        return aiPrompts.map((prompt) => {
            const promptID = prompt.AIPromptID ?? prompt.aiPromptID ?? prompt.aIPromptID;
            const promptLabel = prompt.PromptLabel ?? prompt.promptLabel;

            return {
                value: promptID,
                label: promptLabel,
            };
        }).filter((prompt) => prompt.value !== null && prompt.value !== undefined && prompt.label);
    }, [aiPrompts]);

    const aiSummaryText = useMemo(() => {
        const getSummaryText = (value) => {
            if (value === null || value === undefined) {
                return '';
            }

            if (typeof value === 'string') {
                return value;
            }

            if (Array.isArray(value)) {
                for (const item of value) {
                    const nestedSummary = getSummaryText(item);
                    if (`${nestedSummary}`.trim() !== '') {
                        return nestedSummary;
                    }
                }

                return '';
            }

            if (typeof value === 'object') {
                const directSummary = value.summary ?? value.Summary;
                if (directSummary !== null && directSummary !== undefined) {
                    return typeof directSummary === 'string'
                        ? directSummary
                        : JSON.stringify(directSummary, null, 2);
                }

                const nestedSources = [
                    value.aiSummaryHistory,
                    value.aisummaryhistory,
                    value.aiSummary,
                    value.aisummary
                ];

                for (const source of nestedSources) {
                    const nestedSummary = getSummaryText(source);
                    if (`${nestedSummary}`.trim() !== '') {
                        return nestedSummary;
                    }
                }

                return '';
            }

            return '';
        };

        const summarySource = generatedAISummary ?? aiSummary;

        if (summarySource === null || summarySource === undefined) {
            return '';
        }

        const extractedSummary = getSummaryText(summarySource);
        if (`${extractedSummary}`.trim() !== '') {
            return extractedSummary;
        }

        return typeof summarySource === 'string'
            ? summarySource
            : JSON.stringify(summarySource, null, 2);
    }, [aiSummary, generatedAISummary]);

    const getRecoveryPercentValue = (response) => {
        if (response === null || response === undefined) {
            return null;
        }

        if (typeof response === 'string' || typeof response === 'number') {
            return response;
        }

        if (Array.isArray(response)) {
            return response.length > 0 ? getRecoveryPercentValue(response[0]) : null;
        }

        if (typeof response === 'object') {
            return response.recoveryPercent
                ?? response.recoverypercent
                ?? response.RecoveryPercent
                ?? response.recoveryRate
                ?? response.recoveryrate
                ?? response.RecoveryRate
                ?? response.rate
                ?? response.Rate
                ?? null;
        }

        return null;
    }

    const refreshRecoveryPercent = async () => {
        if (!debtorID) {
            return;
        }

        try {
            const recoveryRateResponse = await FuncFetch(
                `${REACT_APP_API_URL}/Claims/GetLastRecoveryRate?debtorid=${encodeURIComponent(debtorID)}`,
                null,
                'GET',
                null,
                instance,
                accounts
            );

            const refreshedRecoveryPercent = getRecoveryPercentValue(recoveryRateResponse);
            if (refreshedRecoveryPercent !== null && refreshedRecoveryPercent !== undefined) {
                setRecoveryPercent(refreshedRecoveryPercent);
                return;
            }
        } catch (recoveryRateError) {
            console.error('Failed to load recovery percent.', recoveryRateError);
        }

        setRecoveryPercent(initialRecoveryPercent ?? 0);
    }

    useEffect(() => {
        refreshRecoveryPercent();
    // eslint-disable-next-line
    }, [debtorID, initialRecoveryPercent]);

    useEffect(() => {
        if (aiSummary !== null && aiSummary !== undefined) {
            refreshRecoveryPercent();
        }
    // eslint-disable-next-line
    }, [aiSummary]);

    useEffect(()=>{
        if(searchString&&searchString!==''){
            let newListToDisplay = []
            activities.forEach(activity=>{
                if(activity.comments.toUpperCase().includes(searchString.toUpperCase())){
                    newListToDisplay.push(activity)
                }
            })
            setActivityList(newListToDisplay)
        }else if(searchString===''){
            setActivityList(activities)
        }
    // eslint-disable-next-line
    },[searchString])

    useEffect(()=>{
        if(activities[0]){
            setActivityList(activities)
        }
    },[activities])

    const handleSubmit = (event) => {
        event.preventDefault();
        setLoader(true);
        
        if (notes === '') {
            setLoader(false);
        } else {
            FuncFetch(
                `${REACT_APP_API_URL}/Claims/PostClaimNotes`,
                {
                    debtorID,
                    note:encodeURIComponent(notes),
                    PublicNote: isPublic
                },
                'POST',
                notes,
                instance,
                accounts
                )
                .then((json) => {
                    setNotes('');
                    setActivityList(json)
                    setActivities(json)
                    setLoader(false);
                })
                .catch(err=>{
                    setNotes('')
                    setLoader(false)
                    setError('There was an issue, please try again',err.message)
                    console.error('There was an issue, please try again',err)
                })
        }
    }

    const openEmail = (mailto, subject, body) => { 
        // This replaces all the line breaks with the proper line break format for email
        body = body.replace(/(?:\r\n|\r|\n)/g, '%0D%0A');
        // This obviously opens the default mailer
        window.open(`mailto:${mailto}?subject=${subject}&body=${body}`)
     }

    function handleResize(e) {
        if(error){
            setError(null)
        }
        e.target.style.height = ""; /* Reset the height*/
        e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
      };

    const handleOpenNoteEditPopup = (note) => { 
        setSelectedNote(note)
        setEditNotePopupOpen(true)
     }
    
    const handleOpenDeleteConfirmationPopup = (note) => {
        setSelectedNote(note)
        setDeleteNoteConfirmationPopupOpen(true)
    }

    const handleAIPromptChange = async (prompt) => {
        setSelectedAIPrompt(prompt);
        setAIPromptError(null);

        if (!prompt) {
            return;
        }

        if (!debtorID) {
            setAIPromptError('Debtor ID is required.');
            return;
        }

        setRunningAIPrompt(true);

        try {
            const response = await FuncFetch(
                `${REACT_APP_API_URL}/Claims/RunAIPrompt`,
                null,
                'POST',
                {
                    AIPromptID: prompt.value,
                    DebtorID: debtorID,
                },
                instance,
                accounts
            );

            setGeneratedAISummary(response.message);

            if (response?.activities) {
                setActivities(response.activities);
            }
        } catch (err) {
            console.error(err);
            setAIPromptError('There was an issue running the AI prompt, please try again.');
        } finally {
            await refreshRecoveryPercent();
            setRunningAIPrompt(false);
        }
    }

    const handleOpenAISummaryHistoryPopup = () => {
        setAISummaryHistoryPopupOpen(true);
        setAISummaryHistoryError(null);
        setLoadingAISummaryHistory(true);

        if (!debtorID) {
            setLoadingAISummaryHistory(false);
            setAISummaryHistoryError('Debtor ID is required.');
            return;
        }

        FuncFetch(
            `${REACT_APP_API_URL}/Claims/GetAISummaries`,
            { debtorid: debtorID },
            'GET',
            null,
            instance,
            accounts
        )
            .then((response) => {
                const summaries = Array.isArray(response)
                    ? response
                    : response?.aiSummaryHistory ?? response?.aisummaryhistory ?? [];

                setAISummaryHistory(summaries);
            })
            .catch((err) => {
                console.error(err);
                setAISummaryHistoryError('There was an issue loading AI summaries, please try again.');
            })
            .finally(() => {
                setLoadingAISummaryHistory(false);
            });
    }

    return (
        <div>
            {editNotePopupOpen&&<EditNoteModal show={editNotePopupOpen} setShow={setEditNotePopupOpen} note={selectedNote} updateAccountFieldsAndActivities={updateAccountFieldsAndActivities} />}
            {deleteNoteConfirmationPopupOpen&&<DeleteNoteConfirmationModal show={deleteNoteConfirmationPopupOpen} setShow={setDeleteNoteConfirmationPopupOpen} note={selectedNote} updateAccountFieldsAndActivities={updateAccountFieldsAndActivities} />}
            <Modal show={aiSummaryHistoryPopupOpen} onHide={()=>setAISummaryHistoryPopupOpen(false)} size='lg'>
                <Modal.Header closeButton>
                    <Modal.Title>AI Summary History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingAISummaryHistory ?
                        <Spinner animation='border' />
                    :null}
                    {aiSummaryHistoryError ?
                        <Alert variant='warning'>{aiSummaryHistoryError}</Alert>
                    :null}
                    {!loadingAISummaryHistory && !aiSummaryHistoryError && aiSummaryHistory.length === 0 ?
                        <Alert>No AI summaries found.</Alert>
                    :null}
                    {!loadingAISummaryHistory && !aiSummaryHistoryError ? aiSummaryHistory.map((summary, index) => (
                        <div key={`${summary.aiPromptID || 'summary'}-${summary.summaryDate || index}`} className='margin-bottom'>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                                <span>{summary.collector}</span>
                                <span>{summary.summaryDate&&new Date(summary.summaryDate).toLocaleString()}</span>
                            </div>
                            <Form.Control
                                as='textarea'
                                readOnly
                                value={summary.summary || ''}
                                rows={6}
                                className='allow-white-spacing'
                            />
                        </div>
                    )) : null}
                </Modal.Body>
            </Modal>
            <div className='margin-top'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <strong>AI Summary</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Button
                            variant='outline-secondary'
                            size='sm'
                            onClick={handleOpenAISummaryHistoryPopup}
                            disabled={loadingAISummaryHistory}
                        >
                            {loadingAISummaryHistory ? <Spinner animation='border' size='sm' /> : <FaListUl />}
                        </Button>
                        <Button
                            variant='outline-secondary'
                            size='sm'
                            onClick={onOpenTranscripts}
                            title='Show Transcripts'
                            disabled={!hasTranscripts}
                            style={{ backgroundColor: 'white' }}
                        >
                            <FaRobot style={{ color: hasTranscripts ? '#0d6efd' : '#adb5bd' }} />
                        </Button>
                        <span>Predicted Recovery {recoveryPercent ?? 0}%</span>
                        <Button
                            variant='outline-secondary'
                            size='sm'
                            onClick={() => setSummaryExpanded(!summaryExpanded)}
                        >
                            {summaryExpanded ? 'Collapse' : 'Expand'}
                        </Button>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <Select
                        className='full-width'
                        options={aiPromptOptions}
                        value={selectedAIPrompt}
                        onChange={handleAIPromptChange}
                        placeholder='Select AI prompt...'
                        isDisabled={loadingAIPrompts || runningAIPrompt}
                        isLoading={loadingAIPrompts}
                    />
                    {runningAIPrompt ? <Spinner animation='border' size='sm' /> : null}
                </div>
                {aiPromptError ? <Alert variant='warning'>{aiPromptError}</Alert> : null}
                <Form.Control
                    as='textarea'
                    readOnly
                    value={aiSummaryText}
                    rows={summaryExpanded ? 14 : 4}
                    style={{ whiteSpace: 'pre-wrap' }}
                />
            </div>

            <Form.Control placeholder='Search...' onChange={(e)=>setSearchString(e.target.value)}></Form.Control>

            <Form className='margin-top' onSubmit={(e)=>handleSubmit(e)}>
                <Row>
                    <Col sm={10}>
                        <Form.Control 
                            as='textarea' 
                            placeholder='Add Note...' 
                            value={notes} 
                            onChange={(e) => setNotes(e.target.value)} 
                            onInput={(e)=>handleResize(e)}
                        />
                    </Col>
                    <Col sm={2}>
                        <Form.Check label='Public' checked={isPublic} onChange={(e)=>setIsPublic(e.target.checked)} />
                        <Button className='full-width' type="submit" disabled={loader} >{loader ? 'Loading...' : 'Submit'}</Button>
                    </Col>
                </Row>
                {error?
                    <Alert variant='warning'>{error}</Alert>
                :null}
            </Form>

            <div className='notes-wrapper padding card margin-top'>
                {activityList&&activityList.map((item) => (
                <div key={item.activityID} className='note'>
                    <div className='note-details'>
                        <span style={{ marginRight: '5px', color: '#51adf4' }}>{item.fullName}</span>
                        <span style={{ marginLeft: '5px', color: '#51adf4' }}>{item.time&&new Date(item.time).toLocaleString()}</span>
                        <p className='allow-white-spacing note-comment-scroll'>{item.comments}</p>
                    </div>
                    <ButtonGroup vertical className='note-buttons'>
                        <Button variant='outline-success' onClick={()=>openEmail('',`Claim Note Review Request: ${debtorID}`,item.comments)}><MdEmail/></Button>
                        {(accounts[0].username.split('@')[0].trim()===item.collector.trim())?
                            <>
                            <Button variant='outline-primary' onClick={()=>handleOpenNoteEditPopup(item)}><PiPencil/></Button> 
                            <Button variant='outline-danger' onClick={()=>handleOpenDeleteConfirmationPopup(item)}><BiTrash/></Button>
                            </>
                        :null}
                    </ButtonGroup>
                </div>))}
            </div>
        </div>
    )
}

export default Comments
